/**
 * «Номера» (/rooms) — список номеров отеля РОВНО как дизайн manager v3 §4.2
 * «Список номеров»:
 *   topbar: крошки «Номера / Список» + «Все номера · N» + Экспорт + «+ Номер»;
 *   фильтр-чипы: Этаж ▾ · Тип ▾ · статус-счётчики Свободно/Занято/Грязно/OOO;
 *   таблица: № · Тип · Этаж · Статус (chip) · Гость / до · Последняя уборка
 *            (ДД.ММ ЧЧ:ММ, mono; «· в очереди» красным; «—» если нет данных) · →
 *   сортировка по любой колонке (вкл. «Последняя уборка»);
 *   1280 — полная таблица; 1024 — скрыть «Этаж»; 375 — card-mode.
 *
 * Клик по строке → ДЕТАЛЬ НОМЕРА (отдельный экран с «Назад», правило форм):
 * проживания номера + смена статуса (отдельный экран) + история смены статуса
 * с инициатором/источником. Данные — GET /v1/rooms/board (один запрос: статус
 * + чистота + последняя уборка + текущий гость) + GET /v1/rooms/{id}/status-history.
 */
import { useMemo, useState } from 'react';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { RoomBoardEntry } from '@hsc/types';
import { resolveTenantSlug } from '../../lib/tenant-slug.js';
import {
  CLEANLINESS_LABEL,
  ROOM_STATUS_LABEL,
  formatDate,
  formatDateTimeShort,
} from '../../lib/fleet-format.js';
import { useRoomBoard, useRoomTypes } from './useRooms.js';
import { RoomDetail } from './RoomDetail.js';

export interface RoomsBoardProps {
  canManage?: boolean;
}

type SortKey = 'number' | 'type' | 'floor' | 'status' | 'cleaned';
type SortDir = 'asc' | 'desc';

/** Тон chip'а статуса для счётчиков/строк. «Грязно» = по чистоте, не по физ-статусу. */
function statusTone(e: RoomBoardEntry): string {
  if (e.status === 'out_of_order' || e.cleanliness === 'out_of_order') return 'danger';
  if (e.cleanliness === 'dirty') return 'danger';
  if (e.status === 'occupied') return 'busy';
  if (e.status === 'cleaning' || e.cleanliness === 'inspected') return 'warn';
  if (e.status === 'available') return 'ok';
  return 'muted';
}

export function RoomsBoard({ canManage = false }: RoomsBoardProps): JSX.Element {
  const slug = useMemo(() => resolveTenantSlug(), []);
  const [selected, setSelected] = useState<string | null>(null);
  const [floor, setFloor] = useState<number | ''>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>('number');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const { data, isLoading, isError } = useRoomBoard();
  const typesQ = useRoomTypes(slug);
  const typeName = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of typesQ.data?.room_types ?? []) m.set(t.id, t.display_name?.ru ?? t.name);
    return m;
  }, [typesQ.data]);

  const all = useMemo(() => data?.items ?? [], [data]);

  const floors = useMemo(
    () => Array.from(new Set(all.map((r) => r.floor))).sort((a, b) => a - b),
    [all],
  );
  const typeIds = useMemo(
    () => Array.from(new Set(all.map((r) => r.type_id))),
    [all],
  );

  const counts = useMemo(() => {
    let free = 0;
    let busy = 0;
    let dirty = 0;
    let ooo = 0;
    for (const r of all) {
      if (r.status === 'out_of_order' || r.cleanliness === 'out_of_order') ooo += 1;
      else if (r.cleanliness === 'dirty') dirty += 1;
      else if (r.status === 'occupied') busy += 1;
      else if (r.status === 'available') free += 1;
    }
    return { free, busy, dirty, ooo };
  }, [all]);

  const rows = useMemo(() => {
    const filtered = all.filter(
      (r) => (floor === '' || r.floor === floor) && (typeFilter === '' || r.type_id === typeFilter),
    );
    const dir = sortDir === 'asc' ? 1 : -1;
    const cmp = (a: RoomBoardEntry, b: RoomBoardEntry): number => {
      switch (sortKey) {
        case 'number':
          return ((parseInt(a.number, 10) || 0) - (parseInt(b.number, 10) || 0)) * dir;
        case 'type':
          return (typeName.get(a.type_id) ?? '').localeCompare(typeName.get(b.type_id) ?? '') * dir;
        case 'floor':
          return (a.floor - b.floor) * dir;
        case 'status':
          return statusTone(a).localeCompare(statusTone(b)) * dir;
        case 'cleaned': {
          const av = a.last_cleaned_at ? Date.parse(a.last_cleaned_at) : 0;
          const bv = b.last_cleaned_at ? Date.parse(b.last_cleaned_at) : 0;
          return (av - bv) * dir;
        }
        default:
          return 0;
      }
    };
    return [...filtered].sort(cmp);
  }, [all, floor, typeFilter, sortKey, sortDir, typeName]);

  const toggleSort = (key: SortKey): void => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  const sortMark = (key: SortKey): string => (key === sortKey ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  // Деталь номера — ОТДЕЛЬНЫЙ экран (список не виден), «Назад» возвращает.
  // Объявлено ПОСЛЕ всех хуков (rules-of-hooks).
  if (selected) {
    return (
      <RoomDetail
        roomId={selected}
        typeName={typeName.get(all.find((r) => r.room_id === selected)?.type_id ?? '')}
        canManage={canManage}
        onBack={() => setSelected(null)}
      />
    );
  }

  const exportCsv = (): void => {
    const header = ['Номер', 'Тип', 'Этаж', 'Статус', 'Чистота', 'Гость', 'Последняя уборка'];
    const lines = rows.map((r) =>
      [
        r.number,
        typeName.get(r.type_id) ?? '',
        String(r.floor),
        ROOM_STATUS_LABEL[r.status],
        CLEANLINESS_LABEL[r.cleanliness] ?? r.cleanliness,
        r.current_guest_name ?? '',
        formatDateTimeShort(r.last_cleaned_at) ?? '',
      ].join(';'),
    );
    const csv = [header.join(';'), ...lines].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'rooms.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section data-testid="rooms-board">
      <div className="stays-head">
        <div>
          <div className="cb-crumbs">Номера / Список</div>
          <h1 className="hsc-page__title">Все номера · {all.length}</h1>
        </div>
        <div className="hsc-stack" style={{ flexDirection: 'row', gap: '0.5rem' }}>
          <Button variant="ghost" size="sm" onClick={exportCsv} data-testid="rooms-export">
            Экспорт
          </Button>
          {canManage && (
            <span className="ta-muted" data-testid="rooms-add-hint" style={{ fontSize: 12 }}>
              + Номер — в «Настройках»
            </span>
          )}
        </div>
      </div>

      {/* Фильтр-чипы: Этаж · Тип · статус-счётчики */}
      <div className="fleet-statuses" data-testid="rooms-filters" style={{ flexWrap: 'wrap', gap: 6 }}>
        <label className="ta-chip" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          Этаж:
          <select
            value={floor}
            onChange={(e) => setFloor(e.target.value === '' ? '' : Number(e.target.value))}
            data-testid="filter-floor"
            style={{ border: 'none', background: 'transparent' }}
          >
            <option value="">все</option>
            {floors.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </label>
        <label className="ta-chip" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          Тип:
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            data-testid="filter-type"
            style={{ border: 'none', background: 'transparent' }}
          >
            <option value="">все</option>
            {typeIds.map((id) => (
              <option key={id} value={id}>
                {typeName.get(id) ?? id.slice(0, 8)}
              </option>
            ))}
          </select>
        </label>
        <span className="fleet-chip fleet-chip--ok is-active" data-testid="count-free">
          Свободно · {counts.free}
        </span>
        <span className="fleet-chip fleet-chip--busy is-active" data-testid="count-busy">
          Занято · {counts.busy}
        </span>
        <span className="fleet-chip fleet-chip--danger is-active" data-testid="count-dirty">
          Грязно · {counts.dirty}
        </span>
        <span className="fleet-chip fleet-chip--danger is-active" data-testid="count-ooo">
          OOO · {counts.ooo}
        </span>
      </div>

      {isLoading ? (
        <div className="stays-table-wrap" data-testid="rooms-skeleton" style={{ padding: 12 }}>
          {Array.from({ length: 10 }, (_, i) => (
            <div key={i} style={{ padding: '6px 0' }}>
              <Skeleton height={28} radius={8} />
            </div>
          ))}
        </div>
      ) : isError || !data ? (
        <Card>
          <p role="alert">Не удалось загрузить номера. Попробуйте обновить страницу.</p>
        </Card>
      ) : all.length === 0 ? (
        <Card>
          <p data-testid="rooms-empty">Здесь будут номера отеля. Добавьте первый.</p>
        </Card>
      ) : (
        <>
          {/* Десктоп/планшет — таблица */}
          <div className="stays-table-wrap" data-testid="rooms-table-wrap">
            <table className="stays-table">
              <thead>
                <tr>
                  <th onClick={() => toggleSort('number')} style={{ cursor: 'pointer' }} data-testid="th-number">
                    №{sortMark('number')}
                  </th>
                  <th onClick={() => toggleSort('type')} style={{ cursor: 'pointer' }}>
                    Тип{sortMark('type')}
                  </th>
                  <th
                    onClick={() => toggleSort('floor')}
                    style={{ cursor: 'pointer' }}
                    className="rooms-col-floor"
                  >
                    Этаж{sortMark('floor')}
                  </th>
                  <th onClick={() => toggleSort('status')} style={{ cursor: 'pointer' }}>
                    Статус{sortMark('status')}
                  </th>
                  <th>Гость / до</th>
                  <th onClick={() => toggleSort('cleaned')} style={{ cursor: 'pointer' }} data-testid="th-cleaned">
                    Последняя уборка{sortMark('cleaned')}
                  </th>
                  <th aria-label="open" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.room_id}
                    className="stays-table__row"
                    tabIndex={0}
                    onClick={() => setSelected(r.room_id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setSelected(r.room_id);
                    }}
                    data-testid={`room-row-${r.number}`}
                  >
                    <td>
                      <strong>{r.number}</strong>
                    </td>
                    <td>{typeName.get(r.type_id) ?? '—'}</td>
                    <td className="rooms-col-floor">{r.floor}</td>
                    <td>
                      <span
                        className={`fleet-badge fleet-badge--${statusTone(r)}`}
                        data-testid={`room-${r.number}-status`}
                      >
                        {r.cleanliness === 'dirty' && r.status !== 'out_of_order'
                          ? 'Грязно'
                          : ROOM_STATUS_LABEL[r.status]}
                      </span>
                    </td>
                    <td>
                      {r.current_guest_name
                        ? `${r.current_guest_name}${r.current_check_out ? ` · до ${formatDate(r.current_check_out)}` : ''}`
                        : r.status === 'out_of_order' && r.out_of_order_reason
                          ? r.out_of_order_reason
                          : '—'}
                    </td>
                    <td className="rooms-col-cleaned" data-testid={`room-${r.number}-cleaned`}>
                      <LastCleaned entry={r} idPrefix="room" />
                    </td>
                    <td className="stays-table__chev" aria-hidden>
                      →
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Мобайл — карточки (статус + № + последняя уборка) */}
          <ul className="stays-cards" data-testid="rooms-cards">
            {rows.map((r) => (
              <li key={r.room_id}>
                <button
                  type="button"
                  className="stays-card"
                  onClick={() => setSelected(r.room_id)}
                  data-testid={`room-card-${r.number}`}
                >
                  <div className="stays-card__top">
                    <span className="stays-card__name">
                      <strong>№ {r.number}</strong>
                    </span>
                    <span className={`fleet-badge fleet-badge--${statusTone(r)}`}>
                      {r.cleanliness === 'dirty' && r.status !== 'out_of_order'
                        ? 'Грязно'
                        : ROOM_STATUS_LABEL[r.status]}
                    </span>
                  </div>
                  <div className="stays-card__dates">
                    Последняя уборка: <LastCleaned entry={r} idPrefix="card" />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/** Колонка «Последняя уборка»: ДД.ММ ЧЧ:ММ mono; «· в очереди» красным; «—» если нет. */
function LastCleaned({ entry, idPrefix }: { entry: RoomBoardEntry; idPrefix: string }): JSX.Element {
  const t = formatDateTimeShort(entry.last_cleaned_at);
  if (!t) {
    return <span style={{ color: 'var(--hsc-color-text-tertiary)', fontStyle: 'italic' }}>—</span>;
  }
  return (
    <span style={{ fontFamily: "var(--hsc-font-mono, 'IBM Plex Mono', monospace)", fontSize: 12 }}>
      {t}
      {entry.in_cleaning_queue && (
        <span style={{ color: '#803434' }} data-testid={`${idPrefix}-${entry.number}-queue`}>
          {' '}
          · в очереди
        </span>
      )}
    </span>
  );
}
